import { Link } from "@inertiajs/react";
import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { STATUS_LABELS } from "@/components/shared/StatusBadge";
import { cn } from "@/lib/utils";
import { formatDateRange } from "@/lib/format";

/**
 * Sidebar dropdown that lets a school admin switch which campaign they're
 * viewing on the dashboard. Defaults to the school's current_campaign and
 * remembers the selection via a `?campaign=<slug>` query param on the
 * dashboard URL.
 *
 * Props:
 *   campaigns — array of { slug, name, state, start_at, end_at, dashboard_path }
 *   selected_campaign_slug — the slug currently selected (or null)
 *   campaigns_path — link to the full campaigns history index
 *
 * If the school has zero campaigns, the picker renders a subtle "No
 * campaigns yet" chip instead of the dropdown.
 */
export default function CampaignPicker({ campaigns, selected_campaign_slug, campaigns_path }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!campaigns || campaigns.length === 0) {
    return (
      <div className="mt-3 px-3 py-2 rounded-md border border-dashed border-border text-[11px] text-muted-foreground">
        No campaigns yet
      </div>
    );
  }

  const selected =
    campaigns.find((c) => c.slug === selected_campaign_slug) || campaigns[0];

  return (
    <div ref={ref} className="mt-3 relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left rounded-lg border border-border bg-card hover:bg-secondary/50 transition-colors px-3 py-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Campaign: ${selected.name}. Change campaign`}
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="break-words text-sm font-medium leading-snug text-foreground line-clamp-2" title={selected.name}>
              {selected.name}
            </p>
            <CampaignMeta campaign={selected} className="mt-1" />
          </div>
          <ChevronDown
            className={`mt-0.5 h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1.5 z-30 rounded-lg border border-border bg-card shadow-lg py-1 max-h-[24rem] overflow-y-auto"
          role="listbox"
        >
          {campaigns.map((c) => {
            const isSelected = c.slug === selected.slug;
            return (
              <Link
                key={c.slug}
                href={c.dashboard_path}
                onClick={() => setOpen(false)}
                className={`flex items-start gap-2.5 px-3 py-2 transition-colors ${
                  isSelected ? "bg-primary/5" : "hover:bg-secondary/40"
                }`}
                role="option"
                aria-selected={isSelected}
              >
                <div className="shrink-0 w-4 flex items-center justify-center mt-0.5">
                  {isSelected && <Check className="h-3.5 w-3.5 text-primary" strokeWidth={3} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-snug ${isSelected ? "text-primary font-semibold" : "text-foreground font-medium"}`}>
                    {c.name}
                  </p>
                  <CampaignMeta campaign={c} withDates className="mt-0.5" />
                </div>
              </Link>
            );
          })}

          <div className="border-t border-border mt-1 pt-0.5">
            <Link
              href={campaigns_path}
              onClick={() => setOpen(false)}
              className="block px-3 py-1.5 text-xs text-primary hover:underline"
            >
              Manage all campaigns →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

const STATE_DOTS = {
  draft: "border border-muted-foreground/60 bg-transparent",
  early_donations: "bg-primary",
  active: "bg-success",
  grace: "bg-warning",
  locked: "bg-muted-foreground/50",
};

// "● Live" (and the dates underneath): state as a small dot and word, not a pill,
// so a long campaign name keeps the width.
function CampaignMeta({ campaign, withDates = false, className }) {
  const status = campaign.display_state || campaign.state;
  return (
    <p className={cn("flex flex-wrap items-center gap-x-1.5 text-[11px] leading-tight text-muted-foreground", className)}>
      <span className={cn("inline-block h-2 w-2 shrink-0 rounded-full", STATE_DOTS[status])} aria-hidden="true" />
      <span>{STATUS_LABELS[status] || status}</span>
      {withDates && <span className="basis-full font-tabular">{formatDateRange(campaign.start_at, campaign.end_at)}</span>}
    </p>
  );
}
